import {BoxedObjectEditor} from "./BoxedObjectEditor";
import {ResizeHandler} from "./ResizeHandler";
import {ResizeLaneHandler} from "./ResizeLaneHandler";

const LABEL_WIDTH = 24;
const HIT_DISTANCE = 3;

export class SwimlaneEditor extends BoxedObjectEditor {
	/**
	 * @param {ProcessEditor} parent
	 * @param {PSwimlane} lane
	 * @param {PSLCoords} coords
	 */
	constructor(parent, lane, coords) {
		super(parent, true, true);
		this.lane = lane;
		this.coords = coords;
		this.handlers = null;
	}

	get lineWidth() {
		return 1;
	}

	get left() {
		return this.coords.l;
	}

	get top() {
		return this.coords.t;
	}

	set left(val) {
		this.coords.l = val;
	}

	set top(val) {
		this.coords.t = val;
	}

	get selected() {
		return super.selected;
	}

	set selected(s) {
		super.selected = s;
		if (s && this.resizable) {
			this.handlers = [];
			this.handlers.push(new ResizeLaneHandler(this, "left", "top"));
			this.handlers.push(new ResizeLaneHandler(this, "center", "top"));
			this.handlers.push(new ResizeLaneHandler(this, "right", "top"));
			this.handlers.push(new ResizeLaneHandler(this, "left", "middle"));
			this.handlers.push(new ResizeLaneHandler(this, "right", "middle"));
			this.handlers.push(new ResizeLaneHandler(this, "left", "bottom"));
			this.handlers.push(new ResizeLaneHandler(this, "center", "bottom"));
			this.handlers.push(new ResizeLaneHandler(this, "right", "bottom"));
		} else this.handlers = null;
	}

	hit(x, y) {
		if (this.handlers) {
			// If selected check handlers first
			for (let h of this.handlers) {
				if (h.hit(x, y)) return h;
			}
		}

		for (let bx of this.hitBoxes()) {
			if (BoxedObjectEditor.boxHit(bx, x, y)) return this;
		}
		return null;
	}

	hitBoxes() {
		const vertical = this.coords.h > this.coords.w;
		const rect = {
			left: this.coords.l - HIT_DISTANCE,
			top: this.coords.t - HIT_DISTANCE,
			width: this.coords.w + 2*HIT_DISTANCE,
			height: this.coords.h + 2*HIT_DISTANCE
		};
		const boxes = [];
		// top
		let bb = {top: rect.top, left: rect.left, width: rect.width, height: 2 * HIT_DISTANCE};
		if (vertical) bb.height += LABEL_WIDTH;
		boxes.push(bb);
		// left
		bb = {top: rect.top, left: rect.left, width: 2 * HIT_DISTANCE, height: rect.height};
		if (!vertical) bb.width += LABEL_WIDTH;
		boxes.push(bb);
		// right
		boxes.push({top: rect.top, left: rect.left + rect.width - 2 * HIT_DISTANCE, width: 2 * HIT_DISTANCE, height: rect.height});
		// bottom
		boxes.push({top: rect.top + rect.height - 2 * HIT_DISTANCE, left: rect.left, width: rect.width, height: 2 * HIT_DISTANCE});
		return boxes;
	}

	draw(ctx) {
		ctx.save();
		const vertical = this.coords.h > this.coords.w;

		const lw = this.lineWidth;
		const rect = BoxedObjectEditor.strokeBox(this.coords.l + this.coords.w / 2, this.coords.t + this.coords.h / 2, this.coords.w, this.coords.h, lw);
		ctx.fillStyle = "#fffefa";
		if (vertical) {
			ctx.fillRect(rect.left, rect.top, rect.width, LABEL_WIDTH);
		} else {
			ctx.fillRect(rect.left, rect.top, LABEL_WIDTH, rect.height);
		}

		ctx.lineWidth = lw;
		ctx.strokeStyle = this.selected ? S.HI_COLORS[8] : "black";
		ctx.strokeRect(rect.left, rect.top, rect.width, rect.height);


		ctx.fillStyle = "black";
		if (vertical) {
			ctx.beginPath();
			ctx.moveTo(rect.left, rect.top + LABEL_WIDTH);
			ctx.lineTo(rect.left + rect.width, rect.top + LABEL_WIDTH);
			ctx.stroke();
			if (this.lane.name) {
				const textX = rect.left + rect.width / 2;
				const textY = rect.top + LABEL_WIDTH / 2 + 4;
				ctx.textAlign = "center";
				ctx.fillText(this.lane.name, textX, textY, rect.height);
			}

		} else {
			ctx.beginPath();
			ctx.moveTo(rect.left + LABEL_WIDTH, rect.top);
			ctx.lineTo(rect.left + LABEL_WIDTH, rect.top + rect.height);
			ctx.stroke();
			if (this.lane.name) {
				const textX = rect.left + LABEL_WIDTH / 2 + 4;
				const textY = rect.top + rect.height / 2;
				ctx.textAlign = "center";
				ctx.translate(textX, textY);
				ctx.rotate(Math.PI / -2);
				ctx.translate(-textX, -textY);
				ctx.fillText(this.lane.name, textX, textY, rect.height);
			}
		}
		ctx.restore();
	}

	drawHandlers(ctx, pixelWidth = 1) {
		if (this.handlers) {
			for (let h of this.handlers) {
				h.draw(ctx, pixelWidth);
			}
		}
	}

	startDragging() {
		this.startLeft = this.left;
		this.startTop = this.top;
	}

	drag(diffX, diffY, snapsX, snapsY) {
		const snap = {};
		const oldLeft = this.left;
		const oldTop = this.top;
		this.left = this.startLeft + diffX;
		this.top = this.startTop + diffY;
		if (oldLeft !== this.left || oldTop !== this.top) this.positionDragged(oldLeft, oldTop, this.left, this.top);
		return snap;
	}

	verticalSnaps() {
		const vertical = this.coords.h > this.coords.w;
		const sn = [this.top, this.top + this.coords.h];
		if (!vertical) sn.push(this.top + this.coords.h / 2);
		return sn;
	}

	horizontalSnaps() {
		const vertical = this.coords.h > this.coords.w;
		const sn = [this.left, this.left + this.coords.w];
		if (vertical) sn.push(this.left + this.coords.w / 2);
		return sn;
	}

	/**
	 * @param {PSwimlane} sl
	 * @param {PSLCoords} coords
	 * @param {object} defaults
	 */
	static cleanUpCoords(sl, coords, defaults) {
		if (typeof coords.l !== 'number') coords.l = defaults.l;
		if (typeof coords.t !== 'number') {
			coords.t = defaults.t;
			defaults.t += 128;
		}

		if (typeof coords.w !== 'number' || typeof coords.h !== 'number') {
			if (typeof coords.w !== 'number') coords.w = defaults.w;
			if (typeof coords.h !== 'number') coords.h = defaults.h;
		}
	}
}